import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  delay,
  from,
  tap,
  throwError
} from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BillingInfo } from '../../models/team.model';
import { CustomKeycloakService } from '../keycloak.service';
import { ToastNotificationService } from '../toast-notification.service';
import { UserService } from '../user.service';

export interface TokenResponse {
  access: string;
  refresh: string;
  user: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private baseUrl = environment.baseUrlLogin;

  constructor(
    private router: Router,
    private http: HttpClient,
    private toastNotificationService: ToastNotificationService,
    private userService: UserService,
    private keycloakService: CustomKeycloakService
  ) {}

  /**
   * Intercepts an HTTP request and performs necessary modifications and error handling.
   *
   * @param {HttpRequest<any>} request - The original HTTP request to be intercepted.
   * @param {HttpHandler} next - The next handler in the HTTP request chain.
   * @return {Observable<HttpEvent<any>>} - An observable that emits an HTTP event.
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.url.includes('/simulate-503')) {
      // Simulate 503 response
      const errorResponse = new HttpErrorResponse({
        status: 503,
        statusText: 'Service Unavailable',
        url: request.url,
        error: { message: 'Simulated 503 error' }
      });

      this.router.navigate(['503']);
      return throwError(() => errorResponse).pipe(delay(500));
    }

    if (
      request.url.startsWith('/assets/') ||
      request.url.startsWith(`${this.baseUrl}/token/`) ||
      request.url.includes('teams/invitations')
    )
      return next.handle(request);

    let accessToken = this.keycloakService.getStoredAccessToken();
    if (accessToken && this.keycloakService.isStoredTokenValid()) {
      request = this.addToken(request, accessToken);

      const storedUser = this.userService.currentUser;
      if (storedUser && (storedUser as any)._keycloak_user) {
        return next.handle(request);
      }
    } else {
      accessToken = this.userService.currentAccessToken;
      if (accessToken) {
        if (!environment.isTesting) {
          request = this.addToken(request, accessToken);
        }
      }
    }

    return from(this.ensureUserData(request)).pipe(
      switchMap((user: any) => {
        let modifiedUrl = request.url;
        try {
          if (user && user.teams && user.teams.length > 0) {
            const firstTeamSlug = user.teams[user.teams.length - 1].slug;
            modifiedUrl = modifiedUrl.replace('{team_slug}', firstTeamSlug);
            modifiedUrl = modifiedUrl.replace('undefined', firstTeamSlug);
          }
        } catch (_error) {
          // Ignore error when replacing team slug
        }
        if (!environment.isTesting) {
          request = this.addToken(request.clone({ url: modifiedUrl }), accessToken || '');
        }
        return next.handle(request).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse) {
              switch (error.status) {
                case HttpStatusCode.Unauthorized:
                  if (request.url.includes(`${this.baseUrl}/token/refresh/`)) {
                    this.userService.clearAuthData();
                    this.toastNotificationService.showNotification({
                      message: 'Unauthorised',
                      type: 'warn'
                    });
                    this.router.navigate(['login']);
                    return throwError(() => new Error('Session expired'));
                  } else {
                    const keycloakToken =
                      this.keycloakService.getStoredAccessToken();
                    if (
                      keycloakToken &&
                      this.keycloakService.isStoredTokenValid()
                    ) {
                      console.warn(
                        'Backend rejected Keycloak token - this is expected during transition period'
                      );
                      return throwError(() => error);
                    }

                    if (keycloakToken) {
                      return this.handleKeycloak401Error(request, next);
                    } else {
                      return this.handle401Error(request, next);
                    }
                  }
                case HttpStatusCode.Forbidden:
                case HttpStatusCode.InternalServerError:
                  return throwError(() => error);
                case HttpStatusCode.ServiceUnavailable:
                  this.router.navigate(['503']);
                  return throwError(() => error);
                default:
                  return throwError(() => error);
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  /**
   * Ensures that user data is available either from local storage or by making a API request
   *
   * @param {HttpRequest<any>} request - The HTTP request object
   *
   * @return {Promise<any>} - A promise that resolves with the user data
   */
  private ensureUserData(request: HttpRequest<any>): Promise<any> {
    return new Promise<any>((resolve) => {
      const storedUser = this.userService.currentUser;
      if (storedUser) {
        resolve(storedUser);
      } else {
        if (this.keycloakService.isStoredTokenValid()) {
          this.fetchKeycloakUserInfo().then((keycloakUser) => {
            if (keycloakUser) {
              const formattedUser =
                this.formatKeycloakUserForLegacy(keycloakUser);
              localStorage.setItem('user', JSON.stringify(formattedUser));
              resolve(formattedUser);
            } else {
              console.warn(
                'Failed to fetch Keycloak user info, resolving with null'
              );
              resolve(null);
            }
          });
        } else if (!request.url.includes(`${this.baseUrl}/users/current/`)) {
          const hasLegacyToken =
            this.userService.currentAccessToken ||
            localStorage.getItem('access_token');
          if (hasLegacyToken) {
            this.fetchCurrentUser().subscribe({
              next: (currentUser) => {
                localStorage.setItem('user', JSON.stringify(currentUser));
                resolve(currentUser);
              },
              error: () => {
                resolve(null);
              }
            });
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      }
    });
  }

  /**
   * Adds the specified token to the authorization header of the request.
   *
   * @param {HttpRequest<any>} request - The request object to add the token to.
   * @param {string} token - The token to be added to the request.
   * @returns {HttpRequest<any>} - The new request object with the token added to the authorization header.
   */
  private addToken(request: HttpRequest<any>, token: string) {
    // eslint-disable-next-line no-constant-condition
    if (false) console.log(token);
    let user = {};
    user = {
      'X-Pomerium-Claim-Sub': '',
      'X-Pomerium-Claim-Email': 'sarah.johnson@bcforestry.ca',
      'X-Pomerium-Claim-Name': 'Sarah Johnson',
      'X-Pomerium-Claim-Iss': 'https://auth.example.com/realms/bc-forestry',
      'X-Pomerium-Claim-Groups': 'research-team'
    };
    if (this.userService.authUser) {
      user = this.userService.authUser;
    } else if (localStorage.getItem('pomeriumAuth')) {
      user = JSON.parse(localStorage.getItem('pomeriumAuth'));
    }
    if (environment.isTesting) {
      user = {};
    }
    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        ...user
      },
      withCredentials: true
    });
  }

  private fetchCurrentUser(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/users/current/`);
  }

  public updateCurrentUser(body: any): Observable<any> {
    return this.http.patch<any>(
      `${environment.baseUrl}/users/profile/update/`,
      body
    );
  }

  public logout() {
    this.userService.clearAuthData();
    this.keycloakService.clearStoredTokens();
    this.router.navigate(['/login']);
  }

  public getBillingInfo(): Observable<BillingInfo> {
    return this.http.get<BillingInfo>(
      `${environment.baseUrl}/{team_slug}/billing/billing_info/`
    );
  }

  /**
   * Fetch user info from Keycloak
   */
  private async fetchKeycloakUserInfo(): Promise<any> {
    try {
      const accessToken = this.keycloakService.getStoredAccessToken();
      if (!accessToken) {
        return null;
      }

      const userInfoUrl = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/userinfo`;

      const response = await fetch(userInfoUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.warn('Failed to fetch Keycloak user info:', response.status);
        return null;
      }
    } catch (error) {
      console.warn('Error fetching Keycloak user info:', error);
      return null;
    }
  }

  /**
   * Format Keycloak user info to match legacy user format
   */
  private formatKeycloakUserForLegacy(keycloakUser: any): any {
    return {
      id: keycloakUser.sub,
      email: keycloakUser.email,
      username: keycloakUser.preferred_username || keycloakUser.email,
      first_name: keycloakUser.given_name || '',
      last_name: keycloakUser.family_name || '',
      is_active: true,
      is_verified: keycloakUser.email_verified || false,
      teams: [
        {
          id: 1, // Default team ID
          name: environment.defaultTeam || 'default',
          slug: environment.defaultTeam || 'default',
          role: 'member'
        }
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _keycloak_user: true,
      _keycloak_data: keycloakUser
    };
  }

  private handleKeycloak401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.keycloakService.refreshStoredToken()).pipe(
      switchMap((refreshSuccess: boolean) => {
        this.isRefreshing = false;
        if (refreshSuccess) {
          const newToken = this.keycloakService.getStoredAccessToken();
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken!));
        } else {
          console.warn(
            'Keycloak token refresh failed - this might be expected during transition period'
          );
          return throwError(() => 'Keycloak token refresh failed');
        }
      }),
      catchError((error: any) => {
        this.isRefreshing = false;
        console.warn(
          'Keycloak token refresh error - this might be expected during transition period:',
          error
        );
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      this.isRefreshing = false;
      this.logout();
      this.toastNotificationService.showNotification({
        message: 'Unauthorised',
        type: 'warn'
      });
      return throwError(() => 'Refresh token not found');
    }
    return this.refreshToken(refreshToken).pipe(
      switchMap((tokenResponse: TokenResponse) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(tokenResponse.access);
        localStorage.setItem('access_token', tokenResponse.access);
        return next.handle(this.addToken(request, tokenResponse.access));
      }),
      catchError((error: any) => {
        this.isRefreshing = false;
        this.logout();
        this.toastNotificationService.showNotification({
          message: 'Unauthorised',
          type: 'warn'
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieves tokens for the given username and password.
   *
   * @param {string} username - The username to authenticate with.
   * @param {string} password - The password to authenticate with.
   * @returns {Observable<TokenResponse>} - An Observable that emits the TokenResponse object.
   */
  public getTokens(
    username: string,
    password: string
  ): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(`${this.baseUrl}/token/`, { username, password })
      .pipe(
        tap((tokenResponse: TokenResponse) => {
          this.userService.setUserData(tokenResponse);
        })
      );
  }

  /**
   * Refreshes the access token using the provided refresh token.
   *
   * @param {string} refreshToken - The refresh token.
   * @returns {Observable<TokenResponse>} - The response containing the new access token.
   */
  private refreshToken(refreshToken: string): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(`${this.baseUrl}/token/refresh/`, {
        refresh: refreshToken
      })
      .pipe(
        tap((tokenResponse: TokenResponse) => {
          localStorage.setItem('access_token', tokenResponse.access);
        }),
        catchError((error: any) => {
          this.logout();
          return throwError(() => error);
        })
      );
  }

  public resetPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/password_reset/`, { email });
  }

  public resetPasswordConfirm(
    password: string,
    token: string
  ): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/password_reset/confirm/`, {
      password,
      token
    });
  }
}
